/**
 * React Query hooks for Leads pipelines.
 */

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import api, { LeadsPipeline, LeadsPipelineSummary } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';

export function usePipelines() {
  const query = useQuery<{ pipelines: LeadsPipeline[] }>({
    queryKey: queryKeys.leads.pipelines.all(),
    queryFn: () => api.listLeadsPipelines(),
  });

  useEffect(() => {
    if (query.isError && !query.isFetching) {
      toast({ title: 'Error', description: 'Failed to load pipelines', variant: 'destructive' });
    }
  }, [query.isError, query.isFetching]);

  return {
    pipelines: query.data?.pipelines ?? [],
    loading: query.isLoading,
    refetch: () => query.refetch().then(() => undefined),
  };
}

export function usePipelineSummary(pipelineId: string | null) {
  const query = useQuery<LeadsPipelineSummary>({
    queryKey: queryKeys.leads.deals.pipelineSummary(pipelineId ?? '_none'),
    queryFn: () => api.getLeadsPipelineSummary(pipelineId as string),
    enabled: Boolean(pipelineId),
  });

  useEffect(() => {
    if (query.isError && !query.isFetching) {
      toast({ title: 'Error', description: 'Failed to load pipeline', variant: 'destructive' });
    }
  }, [query.isError, query.isFetching]);

  return {
    summary: query.data ?? null,
    loading: query.isLoading,
    refetch: () => query.refetch().then(() => undefined),
  };
}
